'use client';

import type { AnchorHTMLAttributes, MouseEvent } from 'react';

import { useBoundUrlSource } from '../../client/security/useBoundUrls.ts';
import {
  openOutsideApp,
  outsideAppUrl,
  type OutsideAppReason,
} from '../../shared/browser/inAppBrowser.ts';
import { boundLinkLabel, boundLinkText } from './boundLinkLabel.tsx';

type AnchorProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'target' | 'rel' | 'onAuxClick'>;

export interface OutsideAppLinkProps extends AnchorProps {
  /** The page or mail/phone link. An unsafe address renders the content without a link. */
  href: string | null | undefined;
  /** Why it cannot open in Smart Manager's own browser. */
  reason: OutsideAppReason;
  /** The content is app copy, never data or model text (see InAppLink `appCopy`). */
  appCopy?: boolean;
}

/**
 * A link to what the in-app browser cannot serve: a call, a DRM player, a
 * file download, a mail or phone link. It stays a real anchor (copy-link,
 * focus, screen readers), but every click, middle-click included, goes
 * through openOutsideApp with that click as the user's gesture: on the
 * desktop to the operating system, in a web browser to a new tab or the
 * mail or phone app. A web address must be bound (boundUrlSet.ts); an
 * unbound one is plain text. A mail or phone link carries one address or
 * number only. A search-result or outside-text web address is named by
 * code (its host, boundLinkLabel.tsx), not by its content.
 */
export function OutsideAppLink({ href, reason, appCopy = false, onClick, children, ...anchorProps }: OutsideAppLinkProps) {
  const safeHref = outsideAppUrl(href);
  const isWebPage = !!safeHref && /^https?:/.test(safeHref);
  const source = useBoundUrlSource(isWebPage ? safeHref : null);
  const codeLabel = boundLinkLabel(isWebPage ? safeHref : null, source);
  if (!safeHref || (isWebPage && !source)) {
    return <span className={anchorProps.className} data-nv-unbound-link={safeHref ? 'true' : undefined}>{children}</span>;
  }

  const open = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    openOutsideApp(safeHref, reason, event);
  };

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (event.defaultPrevented) return;
    open(event);
  };

  const handleAuxClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.button === 1) open(event);
  };

  const text = boundLinkText(safeHref, codeLabel, appCopy, children, anchorProps.title, anchorProps['aria-label']);
  return (
    <a
      {...anchorProps}
      title={text.title}
      aria-label={text.ariaLabel}
      href={safeHref}
      {...(isWebPage ? { target: '_blank' } : {})}
      rel="noopener noreferrer"
      onClick={handleClick}
      onAuxClick={handleAuxClick}
    >
      {text.content}
    </a>
  );
}

export default OutsideAppLink;
